import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { missingIdentifierLines } from "../lib/bulkUploadErrors";
import {
  api,
  ApiError,
  type PairSuggestion,
  PARTICIPANT_STATUSES,
  participantDisplayName,
  type PlayerCategory,
  type PlayerProfile,
  type TournamentParticipant,
  type Tournament,
  type TournamentDivision,
  type TournamentGroup,
} from "../lib/api";
import RankMovementBadge from "../components/RankMovementBadge";
import { formatDate } from "../lib/dateFormat";
import { impersonatePlayer } from "../lib/playerImpersonation";
import {
  accountNotOwnedInfo,
  accountNotOwnedLabel,
  accountNotOwnedListNote,
  accountNotOwnedNote,
  canManagePlayerAccount,
} from "../lib/playerAccountOwnership";
import TournamentBottomNav from "../components/TournamentBottomNav";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import GuideHelpButton from "../components/GuideHelpButton";

const STATUS_LABELS: Record<string, string> = {
  pre_registered: "Pre-registrado",
  active: "Activo",
  confirmed: "Confirmado",
  dropped: "Dado de baja",
};

function normalizeIdentifier(value: string): string {
  return value.trim().toUpperCase();
}

function splitDelimitedLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  result.push(current.trim());
  return result;
}

function parseIdentifierLayout(text: string): {
  nationalFederationIds: string[];
  wtnTennisIds: string[];
  playerLookupRows: Array<{ first_name?: string; last_name?: string; email?: string }>;
  rowCount: number;
} {
  const normalizedText = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalizedText.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length === 0) return { nationalFederationIds: [], wtnTennisIds: [], playerLookupRows: [], rowCount: 0 };
  const headerLine = lines[0];
  const delimiter = headerLine.includes("\t")
    ? "\t"
    : headerLine.includes(";") && !headerLine.includes(",")
      ? ";"
      : ",";
  const headers = splitDelimitedLine(headerLine, delimiter).map((header) =>
    normalizeIdentifier(header).replace(/[_-]+/g, " ").replace(/\s+/g, " "),
  );
  const fmtIndex = headers.findIndex((header) => ["ID FMT", "FMT ID", "NATIONAL FEDERATION ID", "NATIONAL_FEDERATION_ID"].includes(header));
  const wtnIndex = headers.findIndex((header) => ["WTN ID", "ID WTN", "WTN TENNIS ID", "WTN_TENNIS_ID"].includes(header));
  const firstNameIndex = headers.findIndex((header) => ["NOMBRE", "FIRST NAME", "FIRST_NAME"].includes(header));
  const lastNameIndex = headers.findIndex((header) => ["APELLIDO", "APELLIDOS", "LAST NAME", "LAST_NAME"].includes(header));
  const emailIndex = headers.findIndex((header) => ["EMAIL", "CORREO", "CORREO ELECTRONICO", "E MAIL"].includes(header));
  if (fmtIndex === -1 && wtnIndex === -1 && firstNameIndex === -1 && lastNameIndex === -1 && emailIndex === -1) {
    throw new Error("El layout debe tener al menos una columna: Nombre, Apellido, Email, ID FMT o WTN ID.");
  }

  const fmtSet = new Set<string>();
  const wtnSet = new Set<string>();
  const lookupRows = new Map<string, { first_name?: string; last_name?: string; email?: string }>();
  for (const line of lines.slice(1)) {
    const columns = splitDelimitedLine(line, delimiter);
    const fmt = fmtIndex >= 0 ? normalizeIdentifier(columns[fmtIndex] || "") : "";
    const wtn = wtnIndex >= 0 ? normalizeIdentifier(columns[wtnIndex] || "") : "";
    const firstName = firstNameIndex >= 0 ? (columns[firstNameIndex] || "").trim() : "";
    const lastName = lastNameIndex >= 0 ? (columns[lastNameIndex] || "").trim() : "";
    const email = emailIndex >= 0 ? (columns[emailIndex] || "").trim().toLowerCase() : "";
    if (fmt) fmtSet.add(fmt);
    if (wtn) wtnSet.add(wtn);
    if (email || firstName || lastName) {
      lookupRows.set(`${email}|${firstName.toLowerCase()}|${lastName.toLowerCase()}`, {
        ...(firstName ? { first_name: firstName } : {}),
        ...(lastName ? { last_name: lastName } : {}),
        ...(email ? { email } : {}),
      });
    }
  }
  return {
    nationalFederationIds: Array.from(fmtSet),
    wtnTennisIds: Array.from(wtnSet),
    playerLookupRows: Array.from(lookupRows.values()),
    rowCount: Math.max(0, lines.length - 1),
  };
}

function downloadTournamentIdentifierLayout() {
  const csv = "\uFEFFNombre,Apellido,Email,ID FMT,WTN ID\nIsaac,Sefami,isaac@example.com,FMT-98765,\nDaniel,Arditi,daniel@example.com,,WTN-ABC123\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "layout_agregar_jugadores_existentes_fmt_wtn.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatStatus(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

const DIVISION_PICK_HINT = "Para inscribir, elige una categoría en «División del torneo».";
const LAYOUT_DIVISION_CHANGED =
  "Cambiaste de categoría mientras se leía el layout. Vuelve a cargarlo para esta categoría.";

interface AdminTournamentPlayersPageProps {
  setupMode?: boolean;
}

export default function AdminTournamentPlayersPage({
  setupMode = false,
}: AdminTournamentPlayersPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId]);
  const { token, impersonateWithToken, user } = useAuth();
  // Soporte (el superadministrador) asigna a la organización del torneo los
  // jugadores que no son de ninguna (Opción A, Eduardo 2026-09-30).
  const isSupport = Boolean(user?.roles?.includes("superadmin"));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const divisionFromQuery = searchParams.get("division");
  const urlDivisionFilter = divisionFromQuery && Number.isInteger(Number(divisionFromQuery)) ? divisionFromQuery : "";
  // Torneo 67 (Eduardo 2026-10-02): la liga ganaba siempre y elegir
  // Categoría 3, «Todas» o «Limpiar» en la página no cambiaba la lista. El
  // filtro se deriva en el mismo render de ?division= (sin copiarlo a estado,
  // así el cambio de arriba nunca pide la categoría vieja); lo único local es
  // «Todas», guardada con la categoría de la liga bajo la que se eligió:
  // caduca en cuanto la liga cambia.
  const [allDivisionsPickedUnder, setAllDivisionsPickedUnder] = useState<string | null>(null);
  if (allDivisionsPickedUnder !== null && allDivisionsPickedUnder !== urlDivisionFilter) {
    setAllDivisionsPickedUnder(null);
  }
  const divisionFilter = allDivisionsPickedUnder === urlDivisionFilter ? "" : urlDivisionFilter;

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [groups, setGroups] = useState<TournamentGroup[]>([]);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [categories, setCategories] = useState<PlayerCategory[]>([]);
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [initialRankInputs, setInitialRankInputs] = useState<
    Record<number, string>
  >({});
  const [currentRankInputs, setCurrentRankInputs] = useState<
    Record<number, string>
  >({});
  const [savingRank, setSavingRank] = useState<Record<number, boolean>>({});
  const [globalPlayers, setGlobalPlayers] = useState<PlayerProfile[]>([]);
  const [globalPendingSearch, setGlobalPendingSearch] = useState("");
  const [globalSearching, setGlobalSearching] = useState(false);
  const [globalLoaded, setGlobalLoaded] = useState(false);
  const [enrollingPlayerId, setEnrollingPlayerId] = useState<number | null>(
    null,
  );
  const [impersonatingParticipantId, setImpersonatingParticipantId] = useState<
    number | null
  >(null);
  const [assigningOwnerless, setAssigningOwnerless] = useState(false);
  // Solo soporte: los inscritos de TODO el torneo que no son de ninguna
  // organización (los da el backend; la lista de abajo puede estar filtrada).
  const [ownerlessPlayerIds, setOwnerlessPlayerIds] = useState<number[]>([]);
  const loadOwnerlessSeqRef = useRef(0);
  const [droppingTeamId, setDroppingTeamId] = useState<number | null>(null);
  const [pairSuggestions, setPairSuggestions] = useState<PairSuggestion[]>([]);
  const [confirmingPairKey, setConfirmingPairKey] = useState<string | null>(null);
  const [playersTableOpen, setPlayersTableOpen] = useState(false);
  const [playersTableAutoOpened, setPlayersTableAutoOpened] = useState(false);
  const loadParticipantsSeqRef = useRef(0);

  // Una categoría concreta va a la liga para que el selector de arriba
  // muestre la misma (conservando el aviso de borrador del state, como
  // AdminTournamentContextNav). «Todas» no quita ?division= — arriba la
  // volverían a poner — y se queda local.
  function handleDivisionFilterChange(value: string) {
    if (!value) {
      setAllDivisionsPickedUnder(urlDivisionFilter);
      return;
    }
    if (value === urlDivisionFilter) {
      setAllDivisionsPickedUnder(null);
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("division", value);
    setSearchParams(nextParams, { replace: true, state: location.state });
  }

  const activeDivision = useMemo(
    () => divisions.find((division) => String(division.id) === divisionFilter) ?? null,
    [divisions, divisionFilter],
  );
  const activePlayMode = activeDivision?.play_mode ?? tournament?.play_mode;
  const isDoubles = activePlayMode === "doubles";
  const participantNoun = isDoubles ? "pareja" : "jugador";
  const participantNounPlural = isDoubles ? "parejas" : "jugadores";
  const participantNounPluralGendered = isDoubles ? "inscritas" : "inscritos";

  // Con categorías y «Todas» elegida (adonde lleva «Limpiar») no hay UNA
  // categoría destino: inscribir mandaría division_id null y crearía una
  // inscripción sin categoría, fuera de grupos y cuadros. Se pide elegir
  // categoría antes de agregar. ?division= en la liga ya dice que el torneo
  // tiene categorías aunque la lista de la página no haya cargado.
  const divisionPickRequired = !divisionFilter && (divisions.length > 0 || urlDivisionFilter !== "");
  // La categoría elegida no cuenta como filtro para el alta rápida: una
  // categoría vacía ofrece agregar jugadores A ESA categoría (antes solo se
  // ofrecía con «Todas», que no tiene destino).
  const hasListFilters = Boolean(search || statusFilter || categoryFilter);
  const showEmptyEnroll =
    !loading && participants.length === 0 && !hasListFilters && !divisionPickRequired;
  const setupCanContinueToGroups = setupMode && participants.length > 0;

  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(
    () => new Set<number>(),
  );
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResolvingIdentifiers, setBulkResolvingIdentifiers] = useState(false);
  // Qué IDs del layout no cruzaron, enumerados — no solo el conteo.
  const [missingIdLines, setMissingIdLines] = useState<string[]>([]);
  // Multicategoría: "ya inscrito" se evalúa contra la CATEGORÍA
  // seleccionada — estar en otra categoría no bloquea agregarlo a esta.
  const targetDivisionId = divisionFilter ? Number(divisionFilter) : null;
  const enrolledProfileIds = new Set<number>();
  participants.forEach((participant) => {
    // Con «Todas» la lista trae todas las categorías: «ya inscrito» = en
    // alguna (no se puede agregar a ninguna hasta elegir una).
    if (!divisionPickRequired && (participant.division_id ?? null) !== targetDivisionId) return;
    if (participant.player_profile_id !== null) {
      enrolledProfileIds.add(participant.player_profile_id);
    }
    participant.team?.members.forEach((member) => {
      enrolledProfileIds.add(member.player_profile_id);
    });
  });

  const loadTournament = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.tournaments.get(token, tournamentId);
      setTournament(response.data.tournament);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el torneo.");
      }
    }
  }, [token, tournamentId]);

  const loadParticipants = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    const requestSeq = ++loadParticipantsSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const [response, groupsResponse] = await Promise.all([
        api.participants.list(token, tournamentId, {
          search: search || undefined,
          status: statusFilter || undefined,
          category_id: categoryFilter ? Number(categoryFilter) : undefined,
          division_id: divisionFilter ? Number(divisionFilter) : undefined,
        }),
        api.groups.list(token, tournamentId, { division_id: divisionFilter ? Number(divisionFilter) : undefined }),
      ]);
      if (requestSeq !== loadParticipantsSeqRef.current) return;
      setParticipants(response.data.participants);
      setGroups(groupsResponse.data.groups);
    } catch (err) {
      // Latest-only también en el error: el fallo tardío de la categoría de
      // antes no pinta su aviso sobre la lista ya cargada.
      if (requestSeq !== loadParticipantsSeqRef.current) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los jugadores.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (requestSeq === loadParticipantsSeqRef.current) setLoading(false);
    }
  }, [token, tournamentId, search, statusFilter, categoryFilter, divisionFilter]);

  // Después de guardar (rank, altas, parejas, bajas) se recarga con el
  // cargador del render MÁS NUEVO, no con el que el clic dejó capturado: si
  // mientras esperaban cambiaron la categoría (arriba o en la página), el
  // capturado pediría la categoría de antes y, con su secuencia más nueva,
  // pisaría la lista de la categoría elegida.
  const loadParticipantsRef = useRef(loadParticipants);
  useLayoutEffect(() => {
    loadParticipantsRef.current = loadParticipants;
  }, [loadParticipants]);
  const reloadParticipants = useCallback(() => loadParticipantsRef.current(), []);
  // Categoría a la vista, para lo que se decide después de un await.
  const divisionFilterRef = useRef(divisionFilter);
  useLayoutEffect(() => {
    divisionFilterRef.current = divisionFilter;
  }, [divisionFilter]);

  const loadCategories = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.common.playerCategories(token);
      setCategories(response.data.categories);
    } catch {
      // non-critical
    }
  }, [token]);

  const loadDivisions = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.tournaments.listDivisions(token, tournamentId);
      setDivisions(response.data.divisions);
    } catch {
      // non-critical
    }
  }, [token, tournamentId]);

  const loadGlobalPlayers = useCallback(
    async (term: string, limit: number = 50) => {
      if (!token) return;
      setGlobalSearching(true);
      try {
        const response = await api.players.search(token, {
          search: term || undefined,
          limit,
        });
        setGlobalPlayers(response.data.players);
        setGlobalLoaded(true);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || "No pudimos cargar los jugadores.");
        } else {
          setError("Error de red. Intenta otra vez.");
        }
      } finally {
        setGlobalSearching(false);
      }
    },
    [token],
  );

  useEffect(() => {
    void loadTournament();
    void loadCategories();
    void loadDivisions();
  }, [loadTournament, loadCategories, loadDivisions]);

  useEffect(() => {
    void loadParticipants();
  }, [loadParticipants]);

  // Soporte: cuántos inscritos de TODO el torneo no son de ninguna
  // organización. Lo cuenta el backend con la misma regla que «Asignar»
  // (todas las categorías, bajas incluidas): la lista de la página puede estar
  // filtrada por una categoría y el aviso contaba solo esa mientras «Asignar»
  // tomaba todo el torneo (revisión: soporte aprobaba 1 y se entregaban 3).
  // Se recuenta cada vez que la lista termina de cargar.
  const loadOwnerless = useCallback(async () => {
    if (!isSupport || !token || Number.isNaN(tournamentId)) return;
    const requestSeq = ++loadOwnerlessSeqRef.current;
    try {
      const response = await api.superadmin.ownerlessPlayers(token, tournamentId);
      if (requestSeq === loadOwnerlessSeqRef.current) setOwnerlessPlayerIds(response.data.ownerless_player_ids);
    } catch {
      // no crítico: sin el conteo no se ofrece el botón
      if (requestSeq === loadOwnerlessSeqRef.current) setOwnerlessPlayerIds([]);
    }
  }, [isSupport, token, tournamentId]);

  useEffect(() => {
    if (!loading) void loadOwnerless();
  }, [loading, loadOwnerless]);

  // Lo que abarca el aviso: todo el torneo, aunque la lista esté filtrada.
  const ownerlessScope = divisions.length > 1 ? "de todas sus categorías, bajas incluidas" : "bajas incluidas";

  // Soporte: los inscritos de este torneo cuya cuenta no es de ninguna
  // organización (los cargó soporte y los inscribió el cliente, o son
  // registros antiguos) pasan a ser de los organizadores del torneo. Se
  // mandan los que soporte vio en el aviso: el backend no asigna más de esos
  // (aunque mientras tanto inscriban a otro sin dueño) y los que ya son de
  // otra organización no cambian.
  async function handleAssignOwnerless(playerIds: number[]) {
    if (!token || !Number.isFinite(tournamentId) || playerIds.length === 0) return;
    const count = playerIds.length;
    const who = count === 1 ? "1 jugador que hoy no es" : `${count} jugadores que hoy no son`;
    const ok = await askConfirm(
      `¿Asignar a la organización de este torneo la cuenta de ${who} de ninguna organización (${ownerlessScope})? ` +
        "Sus organizadores podrán generarles PIN, entrar como ellos y mandarles sus datos de acceso. Los jugadores " +
        "que ya son de otra organización no cambian.",
      { acceptLabel: "Sí, asignar", dismissLabel: "No, regresar" },
    );
    if (!ok) return;
    setAssigningOwnerless(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.superadmin.assignOwnerlessPlayers(token, tournamentId, playerIds);
      const assigned = response.data.assigned_count;
      const assignedIds = new Set(response.data.assigned_player_ids);
      // Un conteo pedido antes de asignar ya no vale; la recarga trae el nuevo.
      loadOwnerlessSeqRef.current += 1;
      setOwnerlessPlayerIds((current) => current.filter((playerId) => !assignedIds.has(playerId)));
      setMessage(
        assigned === 1
          ? "Listo: 1 jugador ahora es de la organización de este torneo."
          : `Listo: ${assigned} jugadores ahora son de la organización de este torneo.`,
      );
      await reloadParticipants();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo asignar." : "Error de red. Intenta otra vez.");
    } finally {
      setAssigningOwnerless(false);
    }
  }
  const ownerlessCount = isSupport ? ownerlessPlayerIds.length : 0;

  useEffect(() => {
    if (playersTableAutoOpened || loading || participants.length === 0 || participants.length > 100) return;
    const isDesktop =
      typeof window === "undefined"
        ? true
        : window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop) return;
    setPlayersTableOpen(true);
    setPlayersTableAutoOpened(true);
  }, [loading, participants.length, playersTableAutoOpened]);

  useEffect(() => {
    if ((showEmptyEnroll || isDoubles) && !globalLoaded && !globalSearching) {
      void loadGlobalPlayers(isDoubles ? "" : "", isDoubles ? 100 : 50);
    }
  }, [showEmptyEnroll, isDoubles, globalLoaded, globalSearching, loadGlobalPlayers]);

  useEffect(() => {
    if (setupMode && !globalLoaded && !globalSearching) {
      void loadGlobalPlayers("", 100);
    }
  }, [setupMode, globalLoaded, globalSearching, loadGlobalPlayers]);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSearch(pendingSearch.trim());
  }

  // Búsqueda mientras se teclea (2026-10-11): el filtro sigue siendo del
  // servidor; «Aplicar» lo hace al instante.
  useEffect(() => {
    const term = pendingSearch.trim();
    if (term === search) return;
    const handle = window.setTimeout(() => setSearch(term), 350);
    return () => window.clearTimeout(handle);
  }, [pendingSearch, search]);

  function handleGlobalSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void loadGlobalPlayers(globalPendingSearch.trim());
  }

  function handleBulkSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void loadGlobalPlayers(globalPendingSearch.trim(), 100);
  }

  function toggleBulkSelected(playerId: number) {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  }

  function selectAllVisible() {
    if (divisionPickRequired) return;
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of globalPlayers) {
        if (!enrolledProfileIds.has(p.id)) {
          next.add(p.id);
        }
      }
      return next;
    });
  }

  function clearBulkSelection() {
    setBulkSelectedIds(new Set());
  }

  async function handleIdentifierLayoutUpload(file: File | null) {
    if (!file || !token || Number.isNaN(tournamentId)) return;
    if (divisionPickRequired) {
      setError(DIVISION_PICK_HINT);
      return;
    }
    setError(null);
    setMessage(null);
    setMissingIdLines([]);
    setBulkResolvingIdentifiers(true);
    // «Ya inscrito» y la preselección son de la categoría del momento de
    // cargar: si la cambian mientras se lee o se buscan los IDs, el resultado
    // se descarta (marcaría y contaría sobre la categoría equivocada).
    const divisionAtUpload = divisionFilter;
    const divisionChanged = () => divisionFilterRef.current !== divisionAtUpload;
    try {
      const text = await file.text();
      if (divisionChanged()) {
        setError(LAYOUT_DIVISION_CHANGED);
        return;
      }
      const parsed = parseIdentifierLayout(text);
      const lookupCount = parsed.nationalFederationIds.length + parsed.wtnTennisIds.length + parsed.playerLookupRows.length;
      if (lookupCount === 0) {
        setError("El layout no tiene Nombre, Apellido, Email, ID FMT ni WTN ID para buscar.");
        return;
      }
      const response = await api.participants.resolveIdentifiers(token, tournamentId, {
        national_federation_ids: parsed.nationalFederationIds,
        wtn_tennis_ids: parsed.wtnTennisIds,
        player_lookup_rows: parsed.playerLookupRows,
        division_id: targetDivisionId,
      });
      if (divisionChanged()) {
        setError(LAYOUT_DIVISION_CHANGED);
        return;
      }
      const { players, missing_national_federation_ids, missing_wtn_tennis_ids, already_enrolled_player_ids } = response.data;
      setGlobalPlayers((current) => {
        const byId = new Map<number, PlayerProfile>();
        for (const player of players) byId.set(player.id, player);
        for (const player of current) byId.set(player.id, player);
        return Array.from(byId.values()).sort((a, b) => a.display_name.localeCompare(b.display_name));
      });
      setGlobalLoaded(true);
      setBulkSelectedIds((current) => {
        const next = new Set(current);
        for (const player of players) {
          if (!already_enrolled_player_ids.includes(player.id) && !enrolledProfileIds.has(player.id)) {
            next.add(player.id);
          }
        }
        return next;
      });
      const selectableMatches = players.filter(
        (player) => !already_enrolled_player_ids.includes(player.id) && !enrolledProfileIds.has(player.id),
      ).length;
      const missingCount = missing_national_federation_ids.length + missing_wtn_tennis_ids.length;
      const alreadyCount = already_enrolled_player_ids.length;
      setMessage(
        `Layout procesado: ${parsed.rowCount} fila${parsed.rowCount === 1 ? "" : "s"}, ${players.length} match${players.length === 1 ? "" : "es"}, ${selectableMatches} seleccionado${selectableMatches === 1 ? "" : "s"}.` +
          (alreadyCount > 0 ? ` ${alreadyCount} ya inscrito${alreadyCount === 1 ? "" : "s"}.` : "") +
          (missingCount > 0 ? ` ${missingCount} ID${missingCount === 1 ? "" : "s"} sin match.` : ""),
      );
      setMissingIdLines(
        missingIdentifierLines(missing_national_federation_ids, missing_wtn_tennis_ids),
      );
    } catch (err) {
      if (divisionChanged()) {
        setError(LAYOUT_DIVISION_CHANGED);
        return;
      }
      setError(err instanceof Error ? err.message : "No se pudo leer el layout.");
    } finally {
      setBulkResolvingIdentifiers(false);
    }
  }

  async function handleBulkEnroll() {
    if (!token || Number.isNaN(tournamentId)) return;
    if (divisionPickRequired) {
      setError(DIVISION_PICK_HINT);
      return;
    }
    const ids = Array.from(bulkSelectedIds).filter(
      (pid) => !enrolledProfileIds.has(pid),
    );
    if (ids.length === 0) return;
    setError(null);
    setMessage(null);
    setBulkSubmitting(true);
    try {
      const response = await api.participants.bulkEnroll(token, tournamentId, {
        player_profile_ids: ids,
        division_id: divisionFilter ? Number(divisionFilter) : null,
      });
      const { added_count, skipped_existing_count, skipped_missing_count } =
        response.data;
      const parts: string[] = [];
      parts.push(
        `${added_count} ${added_count === 1 ? "jugador agregado" : "jugadores agregados"}.`,
      );
      if (skipped_existing_count > 0) {
        parts.push(
          `${skipped_existing_count} ya ${skipped_existing_count === 1 ? "estaba inscrito" : "estaban inscritos"}.`,
        );
      }
      if (skipped_missing_count > 0) {
        parts.push(
          `${skipped_missing_count} no ${skipped_missing_count === 1 ? "se encontró" : "se encontraron"}.`,
        );
      }
      setMessage(parts.join(" "));
      setBulkSelectedIds(new Set());
      await reloadParticipants();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudieron inscribir los jugadores.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBulkSubmitting(false);
    }
  }

  useEffect(() => {
    if (!token || !isDoubles || Number.isNaN(tournamentId)) {
      setPairSuggestions([]);
      return;
    }
    let cancelled = false;
    void api.participants
      .pairSuggestions(token, tournamentId)
      .then((response) => {
        if (!cancelled) setPairSuggestions(response.data.suggestions);
      })
      .catch(() => {
        if (!cancelled) setPairSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, isDoubles, tournamentId, participants]);

  async function handleConfirmPairSuggestion(suggestion: PairSuggestion) {
    if (!token || Number.isNaN(tournamentId)) return;
    const key = `${suggestion.participant_a.participant_id}-${suggestion.participant_b.participant_id}`;
    setError(null);
    setMessage(null);
    setConfirmingPairKey(key);
    try {
      await api.participants.createTeam(token, tournamentId, {
        player_profile_ids: [
          suggestion.participant_a.player_profile_id,
          suggestion.participant_b.player_profile_id,
        ],
        division_id: suggestion.participant_a.division_id ?? null,
      });
      setMessage(`Pareja confirmada: ${suggestion.participant_a.name} / ${suggestion.participant_b.name}.`);
      await reloadParticipants();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo confirmar la pareja.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setConfirmingPairKey(null);
    }
  }

  async function handleCreatePairFromSelection() {
    if (!token || Number.isNaN(tournamentId)) return;
    if (divisionPickRequired) {
      setError(DIVISION_PICK_HINT);
      return;
    }
    const ids = Array.from(bulkSelectedIds).filter(
      (pid) => !enrolledProfileIds.has(pid),
    );
    if (ids.length !== 2) {
      setError("Selecciona exactamente 2 jugadores para formar la pareja.");
      return;
    }
    setError(null);
    setMessage(null);
    setBulkSubmitting(true);
    try {
      await api.participants.createTeam(token, tournamentId, {
        player_profile_ids: ids,
        division_id: divisionFilter ? Number(divisionFilter) : null,
      });
      setMessage("Pareja inscrita. Ya aparece en Parejas inscritas.");
      setBulkSelectedIds(new Set());
      await reloadParticipants();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo crear la pareja.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBulkSubmitting(false);
    }
  }

  async function handleDropDoublesTeam(participant: TournamentParticipant) {
    if (!token) return;
    // Paquete de baja (2026-08-28): los pendientes de la pareja pasan a W/O
    // del rival (regla de negocio: walkover = victoria). Para cancelar en vez
    // de W/O, la baja fina vive en Ver / editar.
    if (
      !(await askConfirm(
        `¿Dar de baja a ${participantDisplayName(participant)}? Sus partidos ` +
          "pendientes pasarán a W/O a favor de la pareja rival.", { acceptLabel: "Sí, dar de baja", tone: "danger" }))
    ) {
      return;
    }
    setError(null);
    setMessage(null);
    setDroppingTeamId(participant.id);
    try {
      await api.participants.deleteTeam(token, tournamentId, participant.id, "walkover");
      setMessage(`${participantDisplayName(participant)} fue dada de baja.`);
      await reloadParticipants();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo dar de baja la pareja.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setDroppingTeamId(null);
    }
  }

  async function handleEnrollExisting(player: PlayerProfile) {
    if (!token) return;
    if (divisionPickRequired) {
      setError(DIVISION_PICK_HINT);
      return;
    }
    setError(null);
    setMessage(null);
    setEnrollingPlayerId(player.id);
    try {
      await api.participants.enroll(token, tournamentId, {
        player_profile_id: player.id,
        division_id: divisionFilter ? Number(divisionFilter) : null,
      });
      setMessage(`${player.display_name} fue inscrito en el torneo.`);
      await reloadParticipants();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo inscribir al jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setEnrollingPlayerId(null);
    }
  }

  async function handleImpersonateParticipant(participant: TournamentParticipant) {
    if (!token || !participant.player) return;
    if (
      !(await askConfirm(
        "Vas a entrar a la plataforma como este jugador (saldrás de tu sesión de admin). ¿Continuar?", { acceptLabel: "Entrar como jugador" }))
    ) {
      return;
    }
    setError(null);
    setMessage(null);
    setImpersonatingParticipantId(participant.id);
    try {
      await impersonatePlayer(token, participant.player.id, impersonateWithToken);
      navigate("/player", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo entrar como jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setImpersonatingParticipantId(null);
    }
  }

  function parseRankValue(raw: string): number | null | "invalid" {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const value = Number(trimmed);
    if (Number.isNaN(value) || !Number.isFinite(value) || value < 1) {
      return "invalid";
    }
    return Math.trunc(value);
  }

  function storedRankValue(value: TournamentParticipant["initial_rank"]): number | null {
    if (value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }

  async function handleSaveRanks(participant: TournamentParticipant) {
    if (!token) return;
    const rawInitial =
      initialRankInputs[participant.id] ??
      (participant.initial_rank != null
        ? String(participant.initial_rank)
        : "");
    const rawCurrent =
      currentRankInputs[participant.id] ??
      (participant.current_rank != null
        ? String(participant.current_rank)
        : "");
    const parsedInitial = parseRankValue(rawInitial);
    const parsedCurrent = parseRankValue(rawCurrent);
    if (parsedInitial === "invalid" || parsedCurrent === "invalid") {
      setError("Los rangos deben ser números mayores a 0.");
      setMessage(null);
      return;
    }
    const payload: { initial_rank?: number | null; current_rank?: number | null } = {};
    if (parsedInitial !== storedRankValue(participant.initial_rank)) {
      payload.initial_rank = parsedInitial;
    }
    if (parsedCurrent !== storedRankValue(participant.current_rank)) {
      payload.current_rank = parsedCurrent;
    }
    if (Object.keys(payload).length === 0) {
      setError(null);
      setMessage("No hay cambios de ranking.");
      return;
    }
    setError(null);
    setMessage(null);
    setSavingRank((prev) => ({ ...prev, [participant.id]: true }));
    try {
      await api.participants.update(token, tournamentId, participant.id, payload);
      setInitialRankInputs((prev) => {
        const next = { ...prev };
        delete next[participant.id];
        return next;
      });
      setCurrentRankInputs((prev) => {
        const next = { ...prev };
        delete next[participant.id];
        return next;
      });
      setMessage("Ranking actualizado.");
      await reloadParticipants();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el ranking.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSavingRank((prev) => ({ ...prev, [participant.id]: false }));
    }
  }

  const groupByParticipantId = new Map<number, string>();
  groups.forEach((group) => {
    group.participants?.forEach((assignment) => {
      groupByParticipantId.set(assignment.participant_id, group.name);
    });
  });

  function participantContact(
    participant: TournamentParticipant,
    field: "phone" | "email" | "gender" | "birthdate" | "national_federation_id" | "wtn_tennis_id" | "club_name" | "state_province" | "city",
  ): string {
    if (participant.player) {
      const value = participant.player[field];
      if (!value) return "—";
      if (field === "gender") {
        return value === "male" ? "Masculino" : value === "female" ? "Femenino" : value;
      }
      if (field === "birthdate") {
        return formatDate(String(value));
      }
      return String(value);
    }
    const values = participant.team?.members
      .map((member) => member.player?.[field])
      .filter((value): value is string => Boolean(value)) ?? [];
    if (values.length === 0) return "—";
    if (field === "gender") {
      return values
        .map((value) => value === "male" ? "Masculino" : value === "female" ? "Femenino" : value)
        .join(" / ");
    }
    return values.join(" / ");
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <>
        {setupMode && <TournamentSetupProgress current={5} />}
        <section className="card">
          <p className="form-error">ID de torneo inválido.</p>
        </section>
      </>
    );
  }

  const visibleSelectableCount = globalPlayers.filter(
    (p) => !enrolledProfileIds.has(p.id),
  ).length;
  const selectedCount = Array.from(bulkSelectedIds).filter(
    (pid) => !enrolledProfileIds.has(pid),
  ).length;

  return (
    <>
      {setupMode && <TournamentSetupProgress current={5} />}
      {setupMode && setupCanContinueToGroups && (
        <section className="card setup-next-step-card">
          <div>
            <p className="card__eyebrow">Inscripciones</p>
            <h2 className="card__title">Jugadores listos</h2>
            <p className="card__subtitle">
              Hay {participants.length} {participantNounPlural} {participantNounPluralGendered}. Regresa al paso de
              inscripciones para controlar pagos antes de definir el formato.
            </p>
          </div>
          <Link
            className="btn"
            to={`/admin/tournaments/${tournamentId}/setup/registrations`}
          >
            Volver a inscripciones
          </Link>
        </section>
      )}
      {isDoubles && pairSuggestions.length > 0 && (
        <section className="card pair-suggestions-card">
          <h2 className="card__title">Sugerencias de parejas ({pairSuggestions.length})</h2>
          <p className="card__subtitle">
            Detectadas desde el link público (se nombraron mutuamente o su contacto coincide).
            Al confirmar, la pareja se inscribe y los individuales del banco se dan de baja solos.
          </p>
          <ul className="pair-suggestion-list">
            {pairSuggestions.map((suggestion) => {
              const key = `${suggestion.participant_a.participant_id}-${suggestion.participant_b.participant_id}`;
              return (
                <li key={key} className="pair-suggestion">
                  <div>
                    <strong>
                      {suggestion.participant_a.name} / {suggestion.participant_b.name}
                      {suggestion.participant_a.division_name ? <span className="muted"> · {suggestion.participant_a.division_name}</span> : null}
                    </strong>
                    <small className="muted">{suggestion.reasons.join(" · ")}</small>
                  </div>
                  <button
                    type="button"
                    className="btn btn--compact"
                    disabled={confirmingPairKey !== null}
                    onClick={() => void handleConfirmPairSuggestion(suggestion)}
                  >
                    {confirmingPairKey === key ? "Confirmando…" : "Confirmar pareja"}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <section className="card adm-players insc-players">
        <div className="adm-players__head">
          <div>
            <span className="adm-players__eyebrow">Jugadores del torneo</span>
            <h1 className="card__title adm-players__title">
              <span className="hint-label">
                {setupMode
                  ? isDoubles ? "Parejas inscritas" : "Jugadores inscritos"
                  : isDoubles ? "Parejas" : "Jugadores"}
                {tournament ? ` · ${tournament.name}` : ""}
                <GuideHelpButton />
              </span>
              {!loading ? <span className="adm-players__count">{participants.length}</span> : null}
            </h1>
            <p className="card__subtitle">
              {setupMode
                ? isDoubles
                  ? "Lista de parejas inscritas. Puedes ajustar ranking o continuar al siguiente paso."
                  : "Lista de jugadores ya inscritos en este torneo. Puedes ajustar ranking o continuar al siguiente paso."
                : isDoubles
                  ? "Parejas de este torneo. Crea equipos de dos jugadores, ajusta ranking y consulta su detalle."
                  : "Inscripciones de este torneo. Puedes agregar jugadores nuevos o existentes, cambiar estatus y consultar historial."}
            </p>
          </div>
          <div className="adm-players__head-actions">
            <Link
              className="adm-players__btn adm-players__btn--primary"
              to={
                isDoubles || setupMode
                  ? "/admin/players"
                  : `/admin/tournaments/${tournamentId}/players/new${(divisionFilter || urlDivisionFilter) ? `?division=${divisionFilter || urlDivisionFilter}` : ""}`
              }
            >
              {isDoubles ? "Crear jugador" : setupMode ? "Crear nuevo jugador" : "Agregar jugador"}
            </Link>
          </div>
        </div>

        <form className="form insc-players__filters" onSubmit={handleSearch}>
          <label className="insc-players__search">
            Buscar {participantNoun}
            <input
              type="search"
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              placeholder={isDoubles ? "Nombre de pareja o integrante" : "Nombre, email o teléfono"}
            />
          </label>
          <label>
            Estatus
            <select className="t123-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {PARTICIPANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {formatStatus(s)}
                </option>
              ))}
            </select>
          </label>
          <label>
            División del torneo
            <select className="t123-select"
              value={divisionFilter}
              onChange={(e) => handleDivisionFilterChange(e.target.value)}
              disabled={divisions.length === 0}
            >
              <option value="">Todas</option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Categoría jugador
            <select className="t123-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              disabled={categories.length === 0}
            >
              <option value="">Todas</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <div className="form__actions insc-players__filter-actions">
            <button className="adm-players__btn adm-players__btn--secondary" type="submit" disabled={loading}>
              {loading ? "Filtrando…" : "Aplicar"}
            </button>
            {(search || statusFilter || categoryFilter || divisionFilter) && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setPendingSearch("");
                  setSearch("");
                  setStatusFilter("");
                  setCategoryFilter("");
                  handleDivisionFilterChange("");
                }}
                disabled={loading}
              >
                Limpiar
              </button>
            )}
          </div>
        </form>

        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}

        {ownerlessCount > 0 ? (
          // Solo soporte: jugadores de TODO el torneo (no solo de la categoría
          // filtrada) cuya cuenta no es de ninguna organización. Aviso fijo
          // fuera de la tabla, para que se vea aunque la categoría elegida
          // esté vacía; el botón los asigna.
          <div className="cta-row" data-no-toast>
            <p className="muted" role="note" data-no-toast>
              {ownerlessCount === 1
                ? `1 jugador de este torneo (${ownerlessScope}) no es de ninguna organización: su cuenta solo la toca soporte.`
                : `${ownerlessCount} jugadores de este torneo (${ownerlessScope}) no son de ninguna organización: su cuenta solo la toca soporte.`}
            </p>
            <button
              type="button"
              className="btn btn--ghost btn--compact"
              onClick={() => void handleAssignOwnerless(ownerlessPlayerIds)}
              disabled={assigningOwnerless}
            >
              {assigningOwnerless ? "Asignando…" : "Asignar a la organización de este torneo"}
            </button>
          </div>
        ) : null}

        {loading ? (
          <p className="muted">Cargando…</p>
        ) : participants.length === 0 ? (
          hasListFilters ? (
            <p className="muted">
              No hay inscripciones que coincidan con los filtros.
            </p>
          ) : divisionPickRequired ? (
            <p className="muted">
              Aún no hay inscripciones en ninguna categoría. Elige una categoría en «División del torneo» para agregar jugadores.
            </p>
          ) : setupMode ? (
            <p className="muted">
              Aún no hay inscripciones. Selecciona jugadores existentes arriba o usa Crear nuevo jugador.
            </p>
          ) : isDoubles ? (
            <p className="muted">
              Aún no hay parejas armadas. Agrega jugadores existentes desde esta pantalla y forma parejas desde Inscripciones → Registros recibidos.
            </p>
          ) : (
            <div>
              <p>
                {activeDivision
                  ? `Aún no hay inscripciones en ${activeDivision.name}. Busca y agrega jugadores existentes a esta categoría desde la lista, o usa Agregar jugador para crear uno nuevo.`
                  : "Aún no hay inscripciones en este torneo. Busca y agrega jugadores existentes desde la lista, o usa Agregar jugador para crear uno nuevo."}
              </p>
              <form className="form" onSubmit={handleGlobalSearch}>
                <label>
                  Buscar jugador existente
                  <input
                    type="search"
                    value={globalPendingSearch}
                    onChange={(e) => setGlobalPendingSearch(e.target.value)}
                    placeholder="Nombre, email o teléfono"
                  />
                </label>
                <div className="form__actions">
                  <button
                    className="btn"
                    type="submit"
                    disabled={globalSearching}
                  >
                    {globalSearching ? "Buscando…" : "Buscar"}
                  </button>
                  {globalPendingSearch && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => {
                        setGlobalPendingSearch("");
                        void loadGlobalPlayers("");
                      }}
                      disabled={globalSearching}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </form>
              {globalPlayers.length === 0 ? (
                <p className="muted">
                  {globalSearching
                    ? "Buscando…"
                    : globalLoaded
                      ? "No hay jugadores que coincidan."
                      : "Cargando jugadores…"}
                </p>
              ) : (
                <ul className="list">
                  {globalPlayers.map((p) => {
                    const isEnrolling = enrollingPlayerId === p.id;
                    return (
                      <li key={p.id}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <div>
                            <strong>{p.display_name}</strong>
                            {p.primary_category && (
                              <span className="muted">
                                {" "}
                                · {p.primary_category.name}
                              </span>
                            )}
                            {p.city && (
                              <span className="muted"> · {p.city}</span>
                            )}
                            {p.phone && (
                              <span className="muted"> · {p.phone}</span>
                            )}
                            {p.email && (
                              <span className="muted"> · {p.email}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="btn btn--compact"
                            onClick={() => void handleEnrollExisting(p)}
                            disabled={
                              isEnrolling || enrollingPlayerId !== null
                            }
                          >
                            {isEnrolling ? "Agregando…" : "Agregar"}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )
        ) : (
          <details
            className="group-accordion tournament-players-table-accordion"
            open={playersTableOpen}
            onToggle={(event) => setPlayersTableOpen(event.currentTarget.open)}
          >
            <summary className="group-accordion__summary">
              <span className="group-accordion__title">Tabla de {participantNounPlural} {participantNounPluralGendered}</span>
              <span className="group-accordion__meta">
                {participants.length} {participantNounPlural}
              </span>
              <span className="group-accordion__chevron" aria-hidden="true">⌄</span>
            </summary>
            <div className="group-accordion__body">
              {participants.some((item) => item.player && !canManagePlayerAccount(item.player)) ? (
                // Aviso fijo (no un toast): por qué el nombre de algunos
                // jugadores no abre «Entrar como jugador» (Opción A, Eduardo
                // 2026-09-30).
                <p className="muted account-not-owned-note" role="note" data-no-toast>
                  {accountNotOwnedListNote(participants.map((item) => item.player))}
                </p>
              ) : null}
              <div className="responsive-table-wrap">
                <table className="data-table tournament-players-table insc-players__table">
              <thead>
                <tr>
                  <th>{isDoubles ? "Pareja" : "Jugador"}</th>
                  <th>División</th>
                  <th>Grupo</th>
                  <th>Rank inicial</th>
                  <th>Rank actual</th>
                  <th>Cambio</th>
                  <th>Estatus</th>
                  <th>Categoría</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Género</th>
                  <th>Nacimiento</th>
                  <th>ID FMT</th>
                  <th>WTN ID</th>
                  <th>Club</th>
                  <th>Estado</th>
                  <th>Ciudad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.player && !canManagePlayerAccount(p.player) ? (
                        // Lo registró otra organización: el nombre no abre
                        // «Entrar como jugador».
                        <div>
                          <strong>{participantDisplayName(p)}</strong>
                          <div
                            className="muted account-not-owned"
                            data-no-toast
                            title={accountNotOwnedNote(accountNotOwnedInfo(p.player))}
                          >
                            {accountNotOwnedLabel(p.player)}
                          </div>
                        </div>
                      ) : p.player ? (
                        <button
                          type="button"
                          className="player-name-action"
                          onClick={() => void handleImpersonateParticipant(p)}
                          disabled={
                            impersonatingParticipantId !== null ||
                            p.player.active_status !== "active"
                          }
                          title="Entrar al home como este jugador"
                        >
                          <strong>
                            {impersonatingParticipantId === p.id
                              ? "Entrando…"
                              : participantDisplayName(p)}
                          </strong>
                        </button>
                      ) : (
                        <div>
                          <strong>{participantDisplayName(p)}</strong>
                          {p.team?.members.length ? (
                            <div className="muted">
                              {p.team.members.map((member) => member.player?.display_name ?? `Jugador #${member.player_profile_id}`).join(" · ")}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td>{p.division?.name ?? "—"}</td>
                    <td>{groupByParticipantId.get(p.id) ?? "—"}</td>
                    <td>
                      <span className="rank-cell">
                        <input
                          type="number"
                          min={1}
                          className="rank-input"
                          placeholder="—"
                          value={
                            initialRankInputs[p.id] ??
                            (p.initial_rank != null
                              ? String(p.initial_rank)
                              : "")
                          }
                          onChange={(e) =>
                            setInitialRankInputs((prev) => ({
                              ...prev,
                              [p.id]: e.target.value,
                            }))
                          }
                          disabled={savingRank[p.id]}
                        />
                      </span>
                    </td>
                    <td>
                      <span className="rank-cell">
                        <input
                          type="number"
                          min={1}
                          className="rank-input"
                          placeholder="—"
                          value={
                            currentRankInputs[p.id] ??
                            (p.current_rank != null
                              ? String(p.current_rank)
                              : "")
                          }
                          onChange={(e) =>
                            setCurrentRankInputs((prev) => ({
                              ...prev,
                              [p.id]: e.target.value,
                            }))
                          }
                          disabled={savingRank[p.id]}
                        />
                      </span>
                    </td>
                    <td>
                      <RankMovementBadge
                        movement={p.rank_movement}
                        delta={p.rank_delta}
                      />
                    </td>
                    <td>
                      <span
                        className={`status-pill status-pill--${p.current_status}`}
                      >
                        {formatStatus(p.current_status)}
                      </span>
                    </td>
                    <td>{p.current_category?.name ?? "—"}</td>
                    <td>{participantContact(p, "phone")}</td>
                    <td>{participantContact(p, "email")}</td>
                    <td>{participantContact(p, "gender")}</td>
                    <td>{participantContact(p, "birthdate")}</td>
                    <td>{participantContact(p, "national_federation_id")}</td>
                    <td>{participantContact(p, "wtn_tennis_id")}</td>
                    <td>{participantContact(p, "club_name")}</td>
                    <td>{participantContact(p, "state_province")}</td>
                    <td>{participantContact(p, "city")}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--compact"
                          onClick={() => void handleSaveRanks(p)}
                          disabled={savingRank[p.id]}
                        >
                          {savingRank[p.id] ? "Guardando…" : "Guardar rank"}
                        </button>
                        <Link
                          className="btn btn--ghost btn--compact"
                          to={`/admin/tournaments/${tournamentId}/players/${p.id}`}
                        >
                          Ver / editar
                        </Link>
                        {isDoubles && p.current_status !== "dropped" && (
                          <button
                            type="button"
                            className="btn btn--ghost btn--compact"
                            onClick={() => void handleDropDoublesTeam(p)}
                            disabled={droppingTeamId === p.id}
                          >
                            {droppingTeamId === p.id ? "Bajando…" : "Dar de baja"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
                </table>
              </div>
            </div>
          </details>
        )}

      </section>

      {setupMode && (
        <details className="card group-accordion group-accordion--new" open>
          <summary className="group-accordion__summary">
            <span className="group-accordion__title-wrap">
              <span className="group-accordion__title">Agregar jugadores existentes</span>
              <span className="group-accordion__subtitle muted">
                {globalLoaded ? `${globalPlayers.length} disponibles` : ""}
              </span>
            </span>
            <span className="group-accordion__toggle-icon" aria-hidden="true" />
          </summary>

          <div className="group-accordion__body">
            <form className="form" onSubmit={handleBulkSearch}>
              <label>
                Buscar jugador
                <input
                  type="search"
                  value={globalPendingSearch}
                  onChange={(e) => setGlobalPendingSearch(e.target.value)}
                  placeholder="Nombre, email o teléfono"
                />
              </label>
              <div className="form__actions">
                <button
                  className="btn"
                  type="submit"
                  disabled={globalSearching}
                >
                  {globalSearching ? "Buscando…" : "Buscar"}
                </button>
                {globalPendingSearch && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => {
                      setGlobalPendingSearch("");
                      void loadGlobalPlayers("", 100);
                    }}
                    disabled={globalSearching}
                  >
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            </form>

            <div className="identifier-layout-box">
              <div>
                <strong>Carga masiva por ID FMT / WTN ID</strong>
                <p className="muted">
                  Descarga el layout, llena Nombre/Apellido/Email y, si existen, ID FMT o WTN ID. Al cargarlo se marcarán automáticamente los jugadores existentes encontrados.
                </p>
              </div>
              <div className="form__actions identifier-layout-box__actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--compact"
                  onClick={downloadTournamentIdentifierLayout}
                  disabled={bulkResolvingIdentifiers || bulkSubmitting}
                >
                  Descargar layout Excel
                </button>
                <label className="btn btn--ghost btn--compact identifier-layout-box__upload">
                  {bulkResolvingIdentifiers ? "Buscando IDs…" : "Cargar layout"}
                  <input
                    type="file"
                    accept=".csv,.txt,.tsv,text/csv,text/plain"
                    disabled={bulkResolvingIdentifiers || bulkSubmitting || divisionPickRequired}
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      void handleIdentifierLayoutUpload(file);
                      event.currentTarget.value = "";
                    }}
                  />
                </label>
              </div>
              {missingIdLines.length > 0 && (
                <ul className="list">
                  {missingIdLines.map((line) => (
                    <li key={line} className="form-error">{line}</li>
                  ))}
                </ul>
              )}
            </div>

            {divisionPickRequired && (
              <p className="form-error" role="status" style={{ marginTop: "0.5rem" }}>
                {DIVISION_PICK_HINT}
              </p>
            )}

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5rem",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: "0.5rem",
              }}
            >
              <span className="muted">
                {selectedCount} seleccionado{selectedCount === 1 ? "" : "s"}
              </span>
              <div className="form__actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--compact"
                  onClick={selectAllVisible}
                  disabled={
                    globalSearching ||
                    visibleSelectableCount === 0 ||
                    bulkSubmitting ||
                    divisionPickRequired
                  }
                >
                  Seleccionar todos los visibles
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--compact"
                  onClick={clearBulkSelection}
                  disabled={selectedCount === 0 || bulkSubmitting}
                >
                  Limpiar selección
                </button>
                {isDoubles && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => void handleCreatePairFromSelection()}
                    disabled={selectedCount !== 2 || bulkSubmitting || divisionPickRequired}
                    title={
                      divisionPickRequired
                        ? DIVISION_PICK_HINT
                        : selectedCount === 2 ? undefined : "Selecciona exactamente 2 jugadores"
                    }
                  >
                    {bulkSubmitting ? "Inscribiendo…" : "Inscribir como pareja (elige 2)"}
                  </button>
                )}
                <button
                  type="button"
                  className={isDoubles ? "btn btn--ghost" : "btn"}
                  onClick={() => void handleBulkEnroll()}
                  disabled={selectedCount === 0 || bulkSubmitting || divisionPickRequired}
                  title={divisionPickRequired ? DIVISION_PICK_HINT : undefined}
                >
                  {bulkSubmitting
                    ? "Agregando…"
                    : isDoubles
                      ? `Agregar al banco sin pareja${selectedCount > 0 ? ` (${selectedCount})` : ""}`
                      : `Agregar seleccionados${
                          selectedCount > 0 ? ` (${selectedCount})` : ""
                        }`}
                </button>
              </div>
            </div>

            {globalPlayers.length === 0 ? (
              <p className="muted" style={{ marginTop: "0.75rem" }}>
                {globalSearching
                  ? "Buscando…"
                  : globalLoaded
                    ? "No hay jugadores que coincidan."
                    : "Cargando jugadores…"}
              </p>
            ) : (
              <div style={{ marginTop: "0.75rem" }}>
                {/* Barra de scroll superior sincronizada */}
                <div
                  className="table-scroll-sync"
                  id="globalPlayersScrollTop"
                  onScroll={(e) => {
                    const bottom = document.getElementById("globalPlayersScrollBottom");
                    if (bottom) bottom.scrollLeft = e.currentTarget.scrollLeft;
                  }}
                >
                  <div
                    className="table-scroll-sync__inner"
                    style={{ width: `${Math.max(globalPlayers.length * 120, 1200)}px` }}
                  />
                </div>
                <div
                  className="responsive-table-wrap"
                  id="globalPlayersScrollBottom"
                  onScroll={(e) => {
                    const top = document.getElementById("globalPlayersScrollTop");
                    if (top) top.scrollLeft = e.currentTarget.scrollLeft;
                  }}
                >
                  <table className="data-table global-players-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Nombre</th>
                      <th>Apellido</th>
                      <th>Status</th>
                      <th>Teléfono</th>
                      <th>Email</th>
                      <th>Género</th>
                      <th>Nacimiento</th>
                      <th>ID FMT</th>
                      <th>WTN ID</th>
                      <th>Club</th>
                      <th>Estado</th>
                      <th>Ciudad</th>
                      <th>Categoría</th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalPlayers.map((p) => {
                      const alreadyEnrolled = enrolledProfileIds.has(p.id);
                      const selectable = !alreadyEnrolled && !divisionPickRequired;
                      const checked = bulkSelectedIds.has(p.id);
                      return (
                        <tr
                          key={p.id}
                          style={{
                            opacity: alreadyEnrolled ? 0.6 : 1,
                            cursor: selectable ? "pointer" : "not-allowed",
                          }}
                          onClick={() => {
                            if (selectable && !bulkSubmitting) {
                              toggleBulkSelected(p.id);
                            }
                          }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <span className="cell-checkbox-wrap">
                              <input
                                type="checkbox"
                                checked={checked && !alreadyEnrolled}
                                disabled={!selectable || bulkSubmitting}
                                onChange={() => toggleBulkSelected(p.id)}
                              />
                            </span>
                          </td>
                          <td>{p.first_name}</td>
                          <td>{p.last_name || "—"}</td>
                          <td>
                            {alreadyEnrolled ? (
                              <span className="status-pill">Ya inscrito</span>
                            ) : (
                              <span className="status-pill status-pill--draft">Disponible</span>
                            )}
                          </td>
                          <td>{p.phone || "—"}</td>
                          <td>{p.email || "—"}</td>
                          <td>{p.gender === "male" ? "M" : p.gender === "female" ? "F" : p.gender || "—"}</td>
                          <td>{formatDate(p.birthdate)}</td>
                          <td>{p.national_federation_id || "—"}</td>
                          <td>{p.wtn_tennis_id || "—"}</td>
                          <td>{p.club_name || "—"}</td>
                          <td>{p.state_province || "—"}</td>
                          <td>{p.city || "—"}</td>
                          <td>{p.primary_category?.name || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            )}
            <p className="card__subtitle" style={{ marginTop: "0.75rem" }}>
              Si necesitas crear un jugador nuevo, usa{" "}
              <Link to="/admin/players">
                Crear nuevo jugador
              </Link>
              .
            </p>
          </div>
        </details>
      )}

      {setupMode ? (
        <section className="card">
          <p className="card__subtitle">
            Cuando termines de inscribir jugadores, regresa a inscripciones para
            controlar pagos y continuar al formato.
          </p>
          <div className="form__actions">
            <Link
              className="btn"
              to={`/admin/tournaments/${tournamentId}/setup/registrations`}
            >
              Volver a inscripciones
            </Link>
            <Link
              className="btn btn--ghost"
              to={`/admin/tournaments/${tournamentId}/setup/registrations`}
            >
              Cancelar
            </Link>
          </div>
        </section>
      ) : (
        <TournamentBottomNav tournamentId={tournamentId} />
      )}
    </>
  );
}
